import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Upload, FileText, AlertTriangle, CheckCircle2, RefreshCw, Loader2 } from 'lucide-react';

/*
 * Position Analyser - presentation layer only.
 * The calculation engine is fixed and lives in /public/coverage-engine.js
 * (loaded unchanged, exposes window.CoverageEngine.buildModel). This component
 * never recomputes any figure; it binds to the model the engine returns.
 * Data contract documented in the build handoff.
 */

const DASH = '-'; // null placeholder (house rule: no em dashes anywhere)

const CAT_COLORS = {
  income: '#c9956b',      // gold
  spread: '#254370',      // navy-600
  directional: '#2b7a5a', // muted green
};

// ---------- formatting helpers (mirror the reference exactly) ----------
function money(v, d) {
  if (v == null || isNaN(v)) return DASH;
  const n = Math.abs(v);
  const s = '$' + n.toLocaleString('en-US', { minimumFractionDigits: d || 0, maximumFractionDigits: d || 0 });
  return v < 0 ? '(' + s + ')' : s;
}
function moneyK(v) {
  if (v == null || isNaN(v)) return DASH;
  const a = Math.abs(v);
  let s;
  if (a >= 1e6) s = '$' + (a / 1e6).toFixed(2) + 'M';
  else if (a >= 1e3) s = '$' + (a / 1e3).toFixed(0) + 'K';
  else s = '$' + a.toFixed(0);
  return v < 0 ? '(' + s + ')' : s;
}
function num(v, d) {
  if (v == null || isNaN(v)) return DASH;
  return v.toLocaleString('en-US', { minimumFractionDigits: d || 0, maximumFractionDigits: d || 0 });
}
function pct(v, d) {
  if (v == null || isNaN(v)) return DASH;
  return (v * 100).toFixed(d == null ? 1 : d) + '%';
}
function titleCase(s) {
  return String(s).toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}
function uniq(a) {
  const seen = {};
  const out = [];
  a.forEach((x) => { if (!seen[x]) { seen[x] = 1; out.push(x); } });
  return out.sort();
}

// signed percent, colored
function Spct({ v }) {
  if (v == null || isNaN(v)) return <span>{DASH}</span>;
  const s = (v >= 0 ? '+' : '') + (v * 100).toFixed(1) + '%';
  return <span className={v >= 0 ? 'pa-pos' : 'pa-neg'}>{s}</span>;
}

// modeled-value flag (must stay visible per invariant 1)
function Est({ flag, what }) {
  if (!flag) return null;
  return (
    <sup
      className="pa-est"
      title={`${what} modeled with Black-Scholes off this file's own implied vols and spot. The leg is locked inside a platform strategy group, so the export carries no per-leg value for it.`}
      data-testid="estimate-flag"
    >
      m
    </sup>
  );
}

// pick a representative spot to surface in the header
function keySpot(spots) {
  if (!spots) return null;
  if (spots.BE != null) return { root: 'BE', v: spots.BE };
  const k = Object.keys(spots)[0];
  return k ? { root: k, v: spots[k] } : null;
}

// ---------- assignment-risk tape (SVG, reads model only) ----------
function DeltaTape({ model }) {
  const legs = (model.incomeSleeves || []).filter((p) => p.absDelta != null);
  const W = 1000, H = 96, padL = 20, padR = 20, padT = 14, padB = 24;
  const x0 = padL, x1 = W - padR, y0 = padT, y1 = H - padB;
  const X = (d) => x0 + (Math.max(0, Math.min(100, d)) / 100) * (x1 - x0);
  const maxC = Math.max(...legs.map((p) => Math.abs(p.qty)).concat([1]));
  const nb = legs.filter((p) => p.deltaBreach).length;
  return (
    <div style={{ marginTop: '1.25rem' }}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase tracking-wider" style={{ color: 'rgba(248,246,241,0.6)' }}>
          Assignment-risk tape
        </div>
        <div className="pa-tape-legend">
          <i style={{ '--sw': '#5B7186' }}>covered</i>
          <i style={{ '--sw': '#c9956b' }}>watch</i>
          <i style={{ '--sw': '#e0726a' }}>past trigger ({nb})</i>
        </div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img"
           aria-label="Income sleeves plotted by absolute delta against the 60 close trigger"
           style={{ width: '100%', height: '96px' }}>
        <line x1={x0} y1={y1} x2={x1} y2={y1} stroke="rgba(248,246,241,0.25)" strokeWidth="1" />
        {[0, 20, 40, 60, 80, 100].map((g) => {
          const x = X(g);
          const isT = g === 60;
          return (
            <g key={g}>
              <line x1={x} y1={y0} x2={x} y2={y1}
                    stroke={isT ? '#e0726a' : 'rgba(248,246,241,0.15)'}
                    strokeWidth={isT ? 1.4 : 1}
                    strokeDasharray={isT ? '3 3' : undefined} />
              <text x={x} y={H - 7} textAnchor="middle" fontFamily="Roboto Mono" fontSize="9.5"
                    fill={isT ? '#e0726a' : 'rgba(248,246,241,0.5)'}>{g}</text>
            </g>
          );
        })}
        <text x={X(60)} y={y0 + 8} textAnchor="middle" fontFamily="Roboto Mono" fontSize="9" fontWeight="600" fill="#e0726a">CLOSE 60d</text>
        {legs.map((p, i) => {
          const x = X(p.absDelta);
          const hgt = 8 + Math.sqrt(Math.abs(p.qty) / maxC) * (y1 - y0 - 10);
          const yTop = y1 - hgt;
          const col = p.deltaBreach ? '#e0726a' : (p.absDelta >= 45 ? '#c9956b' : '#7c93a8');
          const op = p.deltaBreach ? 0.95 : 0.6;
          const tip = `${p.acctName}  ${p.root} ${p.cp}${p.strike}  |d| ${Math.round(p.absDelta)}  x${Math.abs(p.qty)}${p.deltaEstimated ? '  (modeled)' : ''}`;
          return (
            <rect key={i} x={x - 1.4} y={yTop} width="2.8" height={hgt} rx="1.2" fill={col} opacity={op}>
              <title>{tip}</title>
            </rect>
          );
        })}
      </svg>
    </div>
  );
}

// ---------- category donut ----------
function CategoryDonut({ model }) {
  const c = model.byCategory;
  const segs = [
    { key: 'income', label: 'Income sleeves', hex: CAT_COLORS.income },
    { key: 'spread', label: 'Spreads', hex: CAT_COLORS.spread },
    { key: 'directional', label: 'Directional', hex: CAT_COLORS.directional },
  ].map((x) => ({ ...x, n: c[x.key].legs, val: c[x.key].value }));
  const total = segs.reduce((a, s) => a + s.n, 0) || 1;
  const r = 54, cx = 66, cy = 66, circ = 2 * Math.PI * r;
  let off = 0;
  return (
    <div className="flex items-center gap-6 p-5 flex-wrap">
      <svg width="132" height="132" viewBox="0 0 132 132" className="flex-none">
        {segs.map((s) => {
          const len = (s.n / total) * circ;
          const el = (
            <circle key={s.key} r={r} cx={cx} cy={cy} fill="none" stroke={s.hex} strokeWidth="20"
                    strokeDasharray={`${len} ${circ - len}`} strokeDashoffset={-off}
                    transform={`rotate(-90 ${cx} ${cy})`} />
          );
          off += len;
          return el;
        })}
        <text x="66" y="62" textAnchor="middle" fontFamily="Playfair Display" fontWeight="700" fontSize="24" fill="#15212F">{total}</text>
        <text x="66" y="80" textAnchor="middle" fontFamily="Roboto Mono" fontSize="9.5" fill="#8A95A2">NET LEGS</text>
      </svg>
      <div className="flex-1 min-w-[160px]">
        {segs.map((s) => (
          <div className="pa-legend-row" key={s.key}>
            <span className="pa-sw" style={{ background: s.hex }} />
            <span className="flex-1 text-sm">{s.label}</span>
            <span className="num text-sm font-semibold">{s.n}<span className="block text-xs font-normal" style={{ color: 'var(--muted-foreground)' }}>{moneyK(s.val)}</span></span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- income monitor columns ----------
const INC_COLS = [
  { k: 'acctName', label: 'Account', align: 'l', cell: (p) => p.acctName },
  { k: 'root', label: 'Undl', align: 'l', cell: (p) => p.root },
  { k: 'cp', label: 'P/C', align: 'c', cell: (p) => p.cp },
  { k: 'strike', label: 'Strike', align: 'n', cell: (p) => num(p.strike, p.strike % 1 ? 1 : 0) },
  { k: 'exp', label: 'Expiry', align: 'l', cell: (p) => p.exp.slice(5) },
  { k: 'dte', label: 'DTE', align: 'n', cell: (p) => (p.dte != null ? p.dte : DASH) },
  { k: 'qty', label: 'Net', align: 'n', cell: (p) => num(p.qty) },
  { k: 'mark', label: 'Mark', align: 'n', cell: (p) => <>{money(p.mark, 2)}<Est flag={p.markEstimated} what="Mark" /></> },
  { k: 'buyback', label: 'Buyback', align: 'n', cell: (p) => <>{money(p.buyback)}<Est flag={p.markEstimated} what="Buyback" /></> },
  { k: 'absDelta', label: '|Delta|', align: 'n', cell: (p) => <>{p.absDelta != null ? num(Math.round(p.absDelta)) : DASH}<Est flag={p.deltaEstimated} what="Delta" /></> },
  { k: 'moneyness', label: 'Moneyness', align: 'n', cell: (p) => <Spct v={p.moneyness} /> },
  { k: 'deltaBreach', label: 'Trigger', align: 'c', cell: (p) => p.deltaBreach ? <span className="pa-pill red">close</span> : <span className="pa-pill ghost">hold</span> },
];

function IncomeMonitor({ model }) {
  const [search, setSearch] = useState('');
  const [acct, setAcct] = useState('');
  const [und, setUnd] = useState('');
  const [breachesOnly, setBreachesOnly] = useState(false);
  const [sort, setSort] = useState({ k: null, d: -1 });

  const accts = useMemo(() => uniq(model.incomeSleeves.map((p) => p.acctName)), [model]);
  const unds = useMemo(() => uniq(model.incomeSleeves.map((p) => p.root)), [model]);

  const rows = useMemo(() => {
    const q = search.toLowerCase();
    let r = model.incomeSleeves.filter((p) => {
      if (acct && p.acctName !== acct) return false;
      if (und && p.root !== und) return false;
      if (breachesOnly && !p.deltaBreach) return false;
      if (q) {
        const hay = (p.acctName + ' ' + p.root + ' ' + p.cp + ' ' + p.strike + ' ' + p.exp).toLowerCase();
        if (hay.indexOf(q) < 0) return false;
      }
      return true;
    });
    if (sort.k) {
      const { k, d } = sort;
      r = r.slice().sort((a, b) => {
        let x = a[k], y = b[k];
        if (x == null) x = -Infinity;
        if (y == null) y = -Infinity;
        if (typeof x === 'string') return d * x.localeCompare(y);
        return d * ((x > y) - (x < y));
      });
    }
    return r;
  }, [model, search, acct, und, breachesOnly, sort]);

  const onSort = (k) => setSort((s) => (s.k === k ? { k, d: -s.d } : { k, d: -1 }));

  return (
    <div className="pa-sec" data-testid="income-monitor">
      <div className="pa-sec-h">
        <div className="pa-ttl">Income sleeve monitor</div>
        <div className="pa-meta" data-testid="income-count">{rows.length} of {model.incomeSleeves.length} legs</div>
      </div>
      <div className="pa-controls">
        <input className="pa-input flex-1 min-w-[180px]" type="search" placeholder="search account, underlying, strike"
               value={search} onChange={(e) => setSearch(e.target.value)} data-testid="income-search" />
        <select className="pa-input" value={acct} onChange={(e) => setAcct(e.target.value)} data-testid="income-account-filter">
          <option value="">All accounts</option>
          {accts.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <select className="pa-input" value={und} onChange={(e) => setUnd(e.target.value)} data-testid="income-underlying-filter">
          <option value="">All underlyings</option>
          {unds.map((u) => <option key={u} value={u}>{u}</option>)}
        </select>
        <label className="pa-check">
          <input type="checkbox" checked={breachesOnly} onChange={(e) => setBreachesOnly(e.target.checked)} data-testid="income-breaches-only" />
          breaches only
        </label>
      </div>
      <div className="pa-scroll">
        <table className="pa-table">
          <thead>
            <tr>
              {INC_COLS.map((c) => {
                const arrow = sort.k === c.k ? (sort.d > 0 ? '\u25B2' : '\u25BC') : '\u2195';
                return (
                  <th key={c.k} className={`pa-th-sort ${c.align === 'n' ? 'num' : (c.align === 'c' ? 'c' : '')}`}
                      onClick={() => onSort(c.k)} data-testid={`income-th-${c.k}`}>
                    {c.label}<span className="ar">{arrow}</span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((p, i) => (
              <tr key={i} className={p.deltaBreach ? 'breach' : ''}>
                {INC_COLS.map((c) => {
                  let cls = c.align === 'l' ? '' : (c.align === 'c' ? 'c' : 'num');
                  if (c.k === 'absDelta' && p.deltaBreach) cls += ' d';
                  return <td key={c.k} className={cls}>{c.cell(p)}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- all net positions (collapsible) ----------
function AllPositions({ model }) {
  const [q, setQ] = useState('');
  const rows = useMemo(() => {
    const base = model.positions.slice().sort((a, b) =>
      a.category.localeCompare(b.category) || a.acctName.localeCompare(b.acctName) || a.root.localeCompare(b.root));
    if (!q) return base;
    const s = q.toLowerCase();
    return base.filter((p) => (p.acctName + ' ' + p.category + ' ' + p.root + ' ' + p.cp + ' ' + p.strike + ' ' + p.exp).toLowerCase().indexOf(s) >= 0);
  }, [model, q]);
  return (
    <div className="pa-sec" data-testid="all-positions">
      <details>
        <summary className="cursor-pointer px-5 py-4 select-none" data-testid="all-positions-summary">
          <span className="pa-ttl">All net positions with Greeks</span>
          <span className="ml-3 text-sm" style={{ color: 'var(--muted-foreground)' }}>
            {model.positions.length} legs - reconciles to the Schwab custodian book
          </span>
        </summary>
        <div className="pa-controls">
          <input className="pa-input flex-1 min-w-[180px]" type="search" placeholder="filter positions"
                 value={q} onChange={(e) => setQ(e.target.value)} data-testid="all-positions-search" />
        </div>
        <div className="pa-scroll">
          <table className="pa-table">
            <thead>
              <tr>
                <th>Account</th><th>Category</th><th>Undl</th><th className="c">P/C</th>
                <th className="num">Strike</th><th className="num">Expiry</th><th className="num">DTE</th>
                <th className="num">Net</th><th className="num">Mark</th><th className="num">|Delta|</th><th className="num">Value</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p, i) => (
                <tr key={i}>
                  <td>{p.acctName}</td>
                  <td><span className="pa-pill" style={{ background: CAT_COLORS[p.category] + '22', color: CAT_COLORS[p.category] }}>{p.category}</span></td>
                  <td>{p.root}</td>
                  <td className="c">{p.cp}</td>
                  <td className="num">{num(p.strike, p.strike % 1 ? 1 : 0)}</td>
                  <td className="num">{p.exp.slice(5)}</td>
                  <td className="num">{p.dte != null ? p.dte : DASH}</td>
                  <td className="num">{num(p.qty)}</td>
                  <td className="num">{money(p.mark, 2)}<Est flag={p.markEstimated} what="Mark" /></td>
                  <td className="num">{p.absDelta != null ? num(Math.round(p.absDelta)) : DASH}<Est flag={p.deltaEstimated} what="Delta" /></td>
                  <td className="num">{money(p.value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
}

// ---------- main dashboard ----------
function Dashboard({ model, onReload }) {
  const t = model.totals;
  const breach = t.breaches > 0;
  const spot = keySpot(model.spots);

  return (
    <div className="pa" data-testid="position-dashboard">
      <div className="flex items-center justify-between mb-5">
        <h1 className="font-serif text-3xl text-[var(--navy-800)]">Position Analysis</h1>
        <button onClick={onReload} className="btn-big py-2 px-4 flex items-center gap-2" style={{ width: 'auto' }} data-testid="load-new-file">
          <RefreshCw className="h-4 w-4" /> Load new file
        </button>
      </div>

      {/* Status ribbon */}
      <div className="pa-ribbon" data-testid="status-ribbon">
        <div className="flex flex-wrap items-center justify-between gap-6">
          <div className="pa-verdict">
            <div className={`pa-vnum mono ${breach ? 'flag' : 'ok'}`} data-testid="breach-headline">{breach ? t.breaches : 0}</div>
            <div>
              <div className="text-lg" style={{ color: 'var(--cream)' }}>
                {breach ? 'positions need a managed exit' : 'coverage is within tolerance'}
              </div>
              <div className="text-sm" style={{ color: 'rgba(248,246,241,0.6)' }}>
                {breach
                  ? 'short legs past the delta 60 close trigger, most exposed to assignment'
                  : 'no income sleeve is past the delta 60 close trigger at this snapshot'}
              </div>
            </div>
          </div>
          <div className="pa-readouts">
            <Readout l="As of" v={(model.asOf || 'unknown').replace(/\s+\d{4}$/, '')} />
            <Readout l={spot ? `${spot.root} spot` : 'Spot'} v={spot ? money(spot.v, 2) : DASH} />
            <Readout l="Income buyback" v={moneyK(t.incomeShortPremium)} />
            <Readout l="Net delta" v={num(Math.round(model.portfolio.delta))} />
            <Readout l="Daily theta" v={moneyK(model.portfolio.theta)} />
          </div>
        </div>
        <DeltaTape model={model} />
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        <Kpi l="Net positions" v={num(t.netPositions)} s={`${t.stockPositions} stock - ${t.optionLegs} option legs`} />
        <Kpi l="Income sleeves" v={num(t.incomeLegs)} s="naked short premium legs" />
        <Kpi l="Delta-60 breaches" v={num(t.breaches)} s="need a managed close" flag={t.breaches > 0 ? 'flag-red' : 'flag-green'} />
        <Kpi l="Income buyback" v={moneyK(t.incomeShortPremium)} s="cost to close all naked shorts" />
        <Kpi l="Net portfolio delta" v={num(Math.round(model.portfolio.delta))} s={`share-equiv - ${moneyK(model.portfolio.dollarDelta)} delta$`} />
      </div>

      {/* Modeled-mark note */}
      {t.estimatedMarkLegs > 0 && (
        <div className="pa-note" data-testid="estimate-note">
          <b>{t.estimatedMarkLegs} legs are locked inside platform strategy groups</b> and carry no per-leg value in this export.
          Their mark and delta are modeled with Black-Scholes off the file's own implied vols and spots, and flagged
          <sup className="pa-est"> m</sup> throughout.
        </div>
      )}

      {/* Breach action list */}
      <BreachSection model={model} />

      {/* Category + account */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-0">
        <div className="pa-sec">
          <div className="pa-sec-h">
            <div className="pa-ttl">Book by category</div>
            <div className="pa-meta">net legs - market value</div>
          </div>
          <CategoryDonut model={model} />
        </div>
        <AccountSection model={model} />
      </div>

      {/* Income monitor */}
      <div className="mt-5">
        <IncomeMonitor model={model} />
      </div>

      {/* Spreads */}
      <SpreadSection model={model} />

      {/* All positions */}
      <AllPositions model={model} />

      {/* Footnote */}
      <Footnote model={model} />
    </div>
  );
}

function Readout({ l, v }) {
  return (
    <div className="pa-readout">
      <div className="rl">{l}</div>
      <div className="rv">{v}</div>
    </div>
  );
}
function Kpi({ l, v, s, flag }) {
  return (
    <div className={`pa-kpi ${flag || ''}`}>
      <div className="kl">{l}</div>
      <div className="kv">{v}</div>
      <div className="ks">{s}</div>
    </div>
  );
}

function BreachSection({ model }) {
  const b = model.breaches;
  if (!b.length) {
    return (
      <div className="pa-sec" data-testid="breach-section">
        <div className="pa-sec-h clear">
          <div className="pa-ttl flex items-center gap-2"><CheckCircle2 className="h-5 w-5" style={{ color: '#2b7a5a' }} />Close-order status: all clear</div>
        </div>
        <div className="pa-foot">
          No income sleeve is past the delta 60 close trigger at this snapshot. The naked short book is within tolerance.
        </div>
      </div>
    );
  }
  return (
    <div className="pa-sec" data-testid="breach-section">
      <div className="pa-sec-h alert">
        <div className="pa-ttl flex items-center gap-2"><AlertTriangle className="h-5 w-5" style={{ color: '#b02a2a' }} />{b.length} legs past the delta-60 close trigger</div>
        <div className="pa-meta">priority exits, sorted by delta</div>
      </div>
      <div className="pa-scroll">
        <table className="pa-table">
          <thead>
            <tr>
              <th>Account</th><th>Undl</th><th className="c">P/C</th><th className="num">Strike</th>
              <th className="num">DTE</th><th className="num">Net</th><th className="num">|Delta|</th>
              <th className="num">Moneyness</th><th className="num">Buyback</th><th className="c">State</th>
            </tr>
          </thead>
          <tbody>
            {b.map((p, i) => {
              const itm = p.moneyness != null && p.moneyness < 0;
              return (
                <tr key={i} className="breach">
                  <td>{p.acctName}</td>
                  <td>{p.root}</td>
                  <td className="c">{p.cp}</td>
                  <td className="num">{num(p.strike, p.strike % 1 ? 1 : 0)}</td>
                  <td className="num">{p.dte != null ? p.dte : DASH}</td>
                  <td className="num">{num(p.qty)}</td>
                  <td className="num d">{num(Math.round(p.absDelta))}<Est flag={p.deltaEstimated} what="Delta" /></td>
                  <td className="num"><Spct v={p.moneyness} /></td>
                  <td className="num">{money(p.buyback)}<Est flag={p.markEstimated} what="Mark" /></td>
                  <td className="c">{itm ? <span className="pa-pill red">in the money</span> : <span className="pa-pill amber">at the money</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AccountSection({ model }) {
  const rows = model.byAccount.slice();
  const max = Math.max(...rows.map((a) => Math.abs(a.optionMV) + Math.abs(a.stockMV)).concat([1]));
  return (
    <div className="pa-sec">
      <div className="pa-sec-h">
        <div className="pa-ttl">By account</div>
        <div className="pa-meta">market value exposure</div>
      </div>
      <div className="px-5 py-3">
        <table className="pa-table">
          <thead>
            <tr><th>Account</th><th>Exposure</th><th className="num">MV</th><th className="num">Sleeves</th></tr>
          </thead>
          <tbody>
            {rows.map((a, i) => {
              const exp = Math.abs(a.optionMV) + Math.abs(a.stockMV);
              const w = Math.round(100 * exp / max);
              return (
                <tr key={i}>
                  <td>{a.acctName}{a.breaches > 0 && <span className="pa-pill red ml-2">{a.breaches}</span>}</td>
                  <td style={{ width: '44%' }}><div className="pa-bar"><span style={{ width: w + '%' }} /></div></td>
                  <td className="num">{moneyK(exp)}</td>
                  <td className="num">{a.income}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SpreadSection({ model }) {
  if (!model.spreads.length) return null;
  const rows = model.spreads.slice().sort((a, b) => (b.pctMax || 0) - (a.pctMax || 0));
  return (
    <div className="pa-sec mt-5" data-testid="spread-section">
      <div className="pa-sec-h">
        <div className="pa-ttl">Spread &amp; defined-risk coverage</div>
        <div className="pa-meta">65% of max width highlighted</div>
      </div>
      <div className="pa-scroll">
        <table className="pa-table">
          <thead>
            <tr>
              <th>Account</th><th>Strategy</th><th>Undl</th><th className="num">Expiry</th>
              <th className="num">Contracts</th><th className="num">Current value</th><th className="num">Max width value</th><th className="num">% of max</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((s, i) => {
              const hot = s.widthBreach;
              return (
                <tr key={i} style={hot ? { background: 'rgba(201,149,107,0.12)' } : undefined}>
                  <td>{s.acctName}</td>
                  <td>{titleCase(s.type)}</td>
                  <td>{s.root}</td>
                  <td className="num">{s.exp.slice(5)}</td>
                  <td className="num">{num(s.contracts)}</td>
                  <td className="num">{money(s.currentValue)}</td>
                  <td className="num">{money(s.maxWidthValue)}</td>
                  <td className="num" style={hot ? { color: '#9a6a3c', fontWeight: 700 } : undefined}>{pct(s.pctMax)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Footnote({ model }) {
  const est = model.totals.estimatedMarkLegs || 0;
  return (
    <div className="pa-sec mt-5" data-testid="disclosure-footnote">
      <div className="pa-foot">
        <b>How to read this, and what it cannot show from this file.</b><br />
        Every figure is computed from the uploaded thinkorswim snapshot: marks, Greeks, and underlying spots come straight
        from the file, so no live market data is required. Net positions reconcile leg for leg to the Schwab custodian book.
        Account 8951121 is the Coastwise FA master account, which sits in thinkorswim but not the client export, and any
        empty client account is omitted by thinkorswim.<br /><br />
        <b>Income buyback</b> is the cost to close every naked short option leg, so it is the true assignment-side liability.
        Short legs that sit inside a spread are excluded from that figure, because the long leg caps their risk; they appear
        in the spread coverage table instead.<br /><br />
        Values flagged <sup className="pa-est">m</sup> are modeled, not read from the file. {est} short legs are locked inside
        a platform strategy group (a vertical, strangle, condor or butterfly), and the export carries only a strategy-level
        total for them. For those legs the mark and delta are recomputed with Black-Scholes off the file's own implied vols
        and spots. They are deep in or at the money, where the estimate is intrinsic-dominated and reliable.<br /><br />
        Three parts of the full coverage plan need a different export and are not shown here: <b>live resting-order status</b>
        (which legs already have a GTC close at the exchange) needs the working-orders export, the <b>2x-cost</b> profit target
        needs cost basis, which is blank here, and the <b>income pacing</b> needs the transactions export. The delta-60 and
        65%-of-width triggers are fully computable from this file and shown above.
      </div>
    </div>
  );
}

// ---------- upload (empty) state ----------
function UploadState({ onFile, dragging, setDragging, engineReady }) {
  const inputRef = useRef(null);
  const openPicker = () => inputRef.current && inputRef.current.click();
  const onKey = (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openPicker(); } };
  return (
    <div className="pa" data-testid="position-analysis-upload">
      <h1 className="font-serif text-3xl text-[var(--navy-800)] mb-2">Position Analysis</h1>
      <p className="text-sm mb-6" style={{ color: 'var(--muted-foreground)' }}>
        Upload a thinkorswim positions export (with Greeks). Everything is parsed in your browser; the file is never uploaded.
      </p>
      <div
        className={`pa-drop p-12 text-center ${dragging ? 'drag' : ''}`}
        role="button" tabIndex={0} aria-label="Upload positions CSV"
        onClick={openPicker} onKeyDown={onKey}
        onDragEnter={(e) => { e.preventDefault(); setDragging(true); }}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={(e) => { e.preventDefault(); setDragging(false); }}
        onDrop={(e) => { e.preventDefault(); setDragging(false); if (e.dataTransfer.files[0]) onFile(e.dataTransfer.files[0]); }}
        data-testid="drop-zone"
      >
        <Upload className="h-10 w-10 mx-auto mb-4" style={{ color: 'var(--navy-700)' }} />
        <h2 className="font-serif text-xl text-[var(--navy-800)] mb-1">Drop your positions CSV here</h2>
        <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>or click to choose a file</p>
        <input ref={inputRef} type="file" accept=".csv,text/csv" className="hidden"
               onChange={(e) => { if (e.target.files[0]) onFile(e.target.files[0]); }} data-testid="file-input" />
      </div>
      {!engineReady && (
        <p className="text-xs mt-4 flex items-center gap-2" style={{ color: 'var(--muted-foreground)' }}>
          <Loader2 className="h-3 w-3 animate-spin" /> Loading coverage engine...
        </p>
      )}
      <div className="mt-6 flex items-start gap-2 text-xs" style={{ color: 'var(--muted-foreground)' }}>
        <FileText className="h-4 w-4 mt-0.5 flex-none" />
        <span>The calculation engine reconciles leg for leg to the Schwab custodian book. No live market data is required.</span>
      </div>
    </div>
  );
}

export default function PositionAnalysis() {
  const [model, setModel] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [engineReady, setEngineReady] = useState(typeof window !== 'undefined' && !!window.CoverageEngine);

  // Load the fixed engine from /public exactly once, unchanged.
  useEffect(() => {
    if (window.CoverageEngine) { setEngineReady(true); return; }
    const existing = document.querySelector('script[data-coverage-engine]');
    if (existing) {
      existing.addEventListener('load', () => setEngineReady(true));
      return;
    }
    const s = document.createElement('script');
    s.src = `${process.env.PUBLIC_URL || ''}/coverage-engine.js`;
    s.async = true;
    s.setAttribute('data-coverage-engine', '1');
    s.onload = () => setEngineReady(true);
    s.onerror = () => toast.error('Could not load the coverage engine.');
    document.body.appendChild(s);
  }, []);

  const handleFile = useCallback((file) => {
    if (!file) return;
    if (!window.CoverageEngine) { toast.error('Coverage engine is still loading. Please try again in a moment.'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      let m;
      try {
        m = window.CoverageEngine.buildModel(String(reader.result));
      } catch (err) {
        toast.error('That file could not be parsed. ' + (err && err.message ? err.message : ''));
        return;
      }
      if (m.error) {
        toast.error('This does not look like a thinkorswim positions export. ' + m.error);
        return;
      }
      (m.warnings || []).forEach((w) => toast.warning(w));
      setModel(m);
      try { window.scrollTo(0, 0); } catch (e) { /* noop */ }
    };
    reader.onerror = () => toast.error('Could not read the file.');
    reader.readAsText(file);
  }, []);

  if (!model) {
    return <UploadState onFile={handleFile} dragging={dragging} setDragging={setDragging} engineReady={engineReady} />;
  }
  return <Dashboard model={model} onReload={() => setModel(null)} />;
}
